import "@/lib/i18n";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import api from "@/lib/api";
import { applyBrandTheme } from "@/lib/theme";
import SiteGate from "@/components/SiteGate";

import { useEffect } from "react";
import Home from "@/pages/Home";
import Directory from "@/pages/Directory";
import WalletPass from "@/pages/WalletPass";
import PublicProfile from "@/pages/PublicProfile";
import Incidents from "@/pages/Incidents";
import GiftCard from "@/pages/GiftCard";
import GiftCardCheck from "@/pages/GiftCardCheck";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Login from "@/pages/Login";
import AcceptInvitation from "@/pages/AcceptInvitation";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import RegisterCitizen from "@/pages/RegisterCitizen";
import ConfirmCitizen from "@/pages/ConfirmCitizen";
import UnsubscribeCitizen from "@/pages/UnsubscribeCitizen";
import CitizenPreferences from "@/pages/CitizenPreferences";

import AdminLayout from "@/components/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminAssociates from "@/pages/admin/AdminAssociates";
import AdminIncidents from "@/pages/admin/AdminIncidents";
import AdminGiftCards from "@/pages/admin/AdminGiftCards";
import AdminCampaigns from "@/pages/admin/AdminCampaigns";
import AdminMailings from "@/pages/admin/AdminMailings";
import AdminDocuments from "@/pages/admin/AdminDocuments";
import AdminInvoices from "@/pages/admin/AdminInvoices";
import AdminEvents from "@/pages/admin/AdminEvents";
import AdminNotifications from "@/pages/admin/AdminNotifications";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminCitizens from "@/pages/admin/AdminCitizens";
import AdminInterests from "@/pages/admin/AdminInterests";
import AdminPromotionsReview from "@/pages/admin/AdminPromotionsReview";
import AdminCollaborators from "@/pages/admin/AdminCollaborators";
import AdminUsers from "@/pages/admin/AdminUsers";

import AssociateLayout from "@/components/AssociateLayout";
import AssocDashboard from "@/pages/associate/AssocDashboard";
import AssocServices from "@/pages/associate/AssocServices";
import AssocAgenda from "@/pages/associate/AssocAgenda";
import AssocCard from "@/pages/associate/AssocCard";
import AssocMarketplace from "@/pages/associate/AssocMarketplace";
import AssocInvoices from "@/pages/associate/AssocInvoices";
import AssocProfile from "@/pages/associate/AssocProfile";
import AssocNotifications from "@/pages/associate/AssocNotifications";

function App() {
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => {});
    }
    const loadTheme = () => {
      api.get("/settings").then(({ data }) => {
        applyBrandTheme(data.primary, data.accent);
      }).catch(() => {});
    };
    loadTheme();
    window.addEventListener("ss-settings-updated", loadTheme);
    return () => window.removeEventListener("ss-settings-updated", loadTheme);
  }, []);
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <SiteGate>
        <AuthProvider>
          <BrowserRouter>
          <Toaster richColors position="top-right" />
          <Routes>
            {/* Public */}
            <Route path="/" element={<Home />} />
            <Route path="/directori" element={<Directory />} />
            <Route path="/comerc/:memberNumber" element={<PublicProfile />} />
            <Route path="/wallet/:memberNumber" element={<WalletPass />} />
            <Route path="/invitacio/:token" element={<AcceptInvitation />} />
            <Route path="/incidencies" element={<Incidents />} />
            <Route path="/targeta-regal" element={<GiftCard />} />
            <Route path="/consulta-saldo" element={<GiftCardCheck />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/login" element={<Login />} />
            <Route path="/oblidat" element={<ForgotPassword />} />
            <Route path="/restablir/:token" element={<ResetPassword />} />
            <Route path="/registra't" element={<RegisterCitizen />} />
            <Route path="/registrat" element={<RegisterCitizen />} />
            <Route path="/confirma/:token" element={<ConfirmCitizen />} />
            <Route path="/baixa/:token" element={<UnsubscribeCitizen />} />
            <Route path="/preferencies/:token" element={<CitizenPreferences />} />

            {/* Admin */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="associats" element={<AdminAssociates />} />
              <Route path="usuaris" element={<AdminUsers />} />
              <Route path="incidencies" element={<AdminIncidents />} />
              <Route path="targetes-regal" element={<AdminGiftCards />} />
              <Route path="campanyes" element={<AdminCampaigns />} />
              <Route path="mailings" element={<AdminMailings />} />
              <Route path="documents" element={<AdminDocuments />} />
              <Route path="factures" element={<AdminInvoices />} />
              <Route path="agenda" element={<AdminEvents />} />
              <Route path="notificacions" element={<AdminNotifications />} />
              <Route path="ciutadans" element={<AdminCitizens />} />
              <Route path="interessos" element={<AdminInterests />} />
              <Route path="promocions-pendents" element={<AdminPromotionsReview />} />
              <Route path="collaboradors" element={<AdminCollaborators />} />
              <Route path="configuracio" element={<AdminSettings />} />
            </Route>

            {/* Associate */}
            <Route path="/associat" element={<AssociateLayout />}>
              <Route index element={<AssocDashboard />} />
              <Route path="serveis" element={<AssocServices />} />
              <Route path="agenda" element={<AssocAgenda />} />
              <Route path="targeta" element={<AssocCard />} />
              <Route path="avantatges" element={<AssocMarketplace />} />
              <Route path="factures" element={<AssocInvoices />} />
              <Route path="perfil" element={<AssocProfile />} />
              <Route path="notificacions" element={<AssocNotifications />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      </SiteGate>
    </ThemeProvider>
  );
}

export default App;
